import type { HttpQueryParam, HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import { extractDetail, isRecord, readText } from "@/core/utils";
import { appendReportQuery, parseContentDispositionFileName } from "@/modules/export";
import type { ExportedFile, ExportFormat } from "@/modules/export";
import type { ObraTipo } from "@/modules/recursos";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoConflitoIgnorado,
  CargaTrabalhoEtapas,
  CargaTrabalhoIntervalo,
  CargaTrabalhoObra,
  CargaTrabalhoObraComEquipe,
  CargaTrabalhoObraProfissional,
  CargaTrabalhoObrasData,
  CargaTrabalhoPeriodo,
  CargaTrabalhoProfissional,
  CargaTrabalhoProfissionaisData,
  ListCargaTrabalhoParams,
} from "./carga-trabalho.types";

export interface ExportCargaTrabalhoParams {
  periodo: CargaTrabalhoPeriodo;
  busca?: string;
  /** Rótulos dos filtros da tela, impressos no arquivo. */
  filtros?: string[];
  /** Valores do painel que o core aplica sobre o resultado. */
  painel?: Record<string, string | string[]>;
  format: ExportFormat;
  signal?: AbortSignal;
}

/** Contrato da HU 1779, exposto pelo hub-bff. */
export const CARGA_TRABALHO_PATHS = {
  PROFISSIONAIS: "/api/recursos/alocacoes/profissionais",
  OBRAS: "/api/recursos/alocacoes/obras/profissionais",
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function nullableText(value: unknown): string | null {
  const result = text(value).trim();
  return result ? result : null;
}

function toList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function readNumeric(value: unknown): string {
  return typeof value === "number" ? String(value) : readText(value).trim();
}

function toObraTipos(raw: Record<string, unknown>): ObraTipo[] {
  return toList(raw.tipos)
    .filter(isRecord)
    .map((tipo) => ({
      id: readNumeric(tipo.id),
      codigo: readNumeric(tipo.codigo),
      tipo: readText(tipo.tipo).trim(),
    }));
}

function toIntervalo(raw: unknown): CargaTrabalhoIntervalo | null {
  if (!isRecord(raw)) return null;
  const inicio = text(raw.inicio);
  const fim = text(raw.fim);
  return inicio && fim ? { inicio: inicio.slice(0, 10), fim: fim.slice(0, 10) } : null;
}

function toEtapas(raw: unknown): CargaTrabalhoEtapas {
  const source = isRecord(raw) ? raw : {};
  return {
    planejamento: toIntervalo(source.planejamento),
    obra: toIntervalo(source.obra),
    fechamento: toIntervalo(source.fechamento),
  };
}

function toObra(raw: unknown): CargaTrabalhoObra | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  if (!id) return null;
  const embarcacao = isRecord(raw.embarcacao)
    ? {
        id: text(raw.embarcacao.id),
        nome: text(raw.embarcacao.nome),
        acronimo: text(raw.embarcacao.acronimo) || null,
      }
    : null;
  return {
    id,
    codigo: text(raw.codigo),
    nome: text(raw.nome),
    situacao: text(raw.situacao),
    embarcacao,
    etapas: toEtapas(raw.etapas),
  };
}

function toAlocacao(raw: unknown): CargaTrabalhoAlocacao | null {
  if (!isRecord(raw)) return null;
  const obra = toObra(raw.obra);
  const intervalo = toIntervalo(raw);
  if (!obra || !intervalo) return null;
  return {
    id: text(raw.id) || `${obra.id}-${intervalo.inicio}`,
    papelObraId: nullableText(raw.papelObraId),
    ...intervalo,
    obra,
  };
}

function toConflitoIgnorado(raw: unknown): CargaTrabalhoConflitoIgnorado | null {
  if (!isRecord(raw)) return null;
  const alocacaoIds = toList(raw.alocacaoIds).map(text).filter(Boolean);
  return alocacaoIds.length >= 2 ? { alocacaoIds } : null;
}

function toProfissional(raw: unknown): CargaTrabalhoProfissional | null {
  if (!isRecord(raw)) return null;
  const id = text(raw.id);
  if (!id) return null;
  return {
    id,
    nome: text(raw.nome),
    time: nullableText(raw.time),
    alocacoes: toList(raw.alocacoes)
      .map(toAlocacao)
      .filter((item): item is CargaTrabalhoAlocacao => item !== null),
    indisponibilidades: toList(raw.indisponibilidades)
      .map(toIntervalo)
      .filter((item): item is CargaTrabalhoIntervalo => item !== null),
    conflitosIgnorados: toList(raw.conflitosIgnorados)
      .map(toConflitoIgnorado)
      .filter((item): item is CargaTrabalhoConflitoIgnorado => item !== null),
  };
}

function toObraComEquipe(raw: unknown): CargaTrabalhoObraComEquipe | null {
  const obra = toObra(raw);
  if (!obra || !isRecord(raw)) return null;
  const profissionais = toList(raw.profissionais)
    .map((item): CargaTrabalhoObraProfissional | null => {
      if (!isRecord(item)) return null;
      const intervalo = toIntervalo(item);
      const id = text(item.id);
      if (!intervalo || !id) return null;
      const alocacaoId = text(item.alocacaoId);
      return {
        id,
        ...(alocacaoId ? { alocacaoId } : {}),
        nome: text(item.nome),
        time: nullableText(item.time),
        papelObraId: nullableText(item.papelObraId),
        ...intervalo,
      };
    })
    .filter((item): item is CargaTrabalhoObraProfissional => item !== null);
  const base = { ...obra, tipos: toObraTipos(raw), profissionais };
  if (!Array.isArray(raw.conflitosIgnorados)) return base;
  const conflitosIgnorados = raw.conflitosIgnorados
    .map(toConflitoIgnorado)
    .filter((item): item is CargaTrabalhoConflitoIgnorado => item !== null);
  return { ...base, conflitosIgnorados };
}

function buildConfig({
  periodo,
  filters = {},
  profissionalIds,
  signal,
}: ListCargaTrabalhoParams): HttpRequestConfig {
  const params: Record<string, HttpQueryParam> = {
    inicio: periodo.inicio,
    fim: periodo.fim,
  };
  const busca = filters.busca?.trim();
  if (busca) params.busca = busca;
  if (filters.obraId) params.obraId = filters.obraId;
  if (filters.embarcacaoId) params.embarcacaoId = filters.embarcacaoId;
  const situacao = filters.situacao?.trim();
  if (situacao) params.situacao = situacao;
  const ids = [...new Set(profissionalIds?.filter(Boolean) ?? [])];
  if (ids.length > 0) params.profissionalIds = ids;
  return { signal, params };
}

function readPeriodo(raw: unknown, fallback: CargaTrabalhoPeriodo): CargaTrabalhoPeriodo {
  return toIntervalo(raw) ?? fallback;
}

/** Carga de trabalho agrupada por profissional (período obrigatório). */
export async function listCargaTrabalhoProfissionais(
  request: ListCargaTrabalhoParams,
): Promise<CargaTrabalhoProfissionaisData> {
  const response = await getHttpClient().get<unknown>(
    CARGA_TRABALHO_PATHS.PROFISSIONAIS,
    buildConfig(request),
  );
  const data = extractDetail(response.data);
  const source = isRecord(data) ? data : {};
  return {
    periodo: readPeriodo(source.periodo, request.periodo),
    profissionais: toList(source.profissionais)
      .map(toProfissional)
      .filter((item): item is CargaTrabalhoProfissional => item !== null),
  };
}

/** Carga de trabalho agrupada por obra, com os profissionais alocados. */
export async function listCargaTrabalhoObras(
  request: ListCargaTrabalhoParams,
): Promise<CargaTrabalhoObrasData> {
  const response = await getHttpClient().get<unknown>(
    CARGA_TRABALHO_PATHS.OBRAS,
    buildConfig(request),
  );
  const data = extractDetail(response.data);
  const source = isRecord(data) ? data : {};
  return {
    periodo: readPeriodo(source.periodo, request.periodo),
    obras: toList(source.obras)
      .map(toObraComEquipe)
      .filter((item): item is CargaTrabalhoObraComEquipe => item !== null),
  };
}

function buildExportConfig({
  periodo,
  busca,
  filtros,
  painel,
  format,
  signal,
}: ExportCargaTrabalhoParams): HttpRequestConfig {
  const params: Record<string, HttpQueryParam> = {
    inicio: periodo.inicio,
    fim: periodo.fim,
    formato: format,
  };
  const term = busca?.trim();
  if (term) params.busca = term;
  appendReportQuery(params, filtros, painel);
  return { signal, params };
}

/** Exportar Visualização — carga agrupada por profissional (US-2389). */
export async function exportCargaTrabalhoProfissionais(
  params: ExportCargaTrabalhoParams,
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(
    `${CARGA_TRABALHO_PATHS.PROFISSIONAIS}/exportacao`,
    { ...buildExportConfig(params), responseType: "arraybuffer" },
  );
  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/** Exportar Visualização — carga agrupada por obra/profissionais (US-2389). */
export async function exportCargaTrabalhoObras(
  params: ExportCargaTrabalhoParams,
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(
    `${CARGA_TRABALHO_PATHS.OBRAS}/exportacao`,
    { ...buildExportConfig(params), responseType: "arraybuffer" },
  );
  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}
